/**
 * PageTargeting — URL pattern matching + SPA route change detection.
 *
 * Determines whether the widget should be active on the current page,
 * and resolves per-page inline container selectors.
 */

export interface PagePattern {
  pattern: string
  container?: string
  label?: string
}

/** Normalize raw patterns from server (can be string or object). */
export function normalizePatterns(
  raw: (string | { pattern: string; container?: string; label?: string })[] | null | undefined,
): PagePattern[] {
  if (!raw || raw.length === 0) return [{ pattern: '*' }]
  return raw.map(p => (typeof p === 'string' ? { pattern: p } : p))
}

/** Check if a pathname matches a single pattern. */
export function matchPattern(pathname: string, pattern: string): boolean {
  if (pattern === '*') return true

  // Exact match
  if (!pattern.endsWith('/*')) {
    // Normalize trailing slashes for comparison
    const normPath = pathname.replace(/\/+$/, '') || '/'
    const normPattern = pattern.replace(/\/+$/, '') || '/'
    return normPath === normPattern
  }

  // Wildcard suffix: /docs/* matches /docs, /docs/, /docs/anything
  const prefix = pattern.slice(0, -2) // remove /*
  const normPath = pathname.replace(/\/+$/, '') || '/'
  const normPrefix = prefix.replace(/\/+$/, '') || '/'
  return normPath === normPrefix || normPath.startsWith(normPrefix + '/')
}

/** Find the first matching pattern for the current pathname. Returns null if none match. */
export function findMatch(pathname: string, patterns: PagePattern[]): PagePattern | null {
  for (const p of patterns) {
    if (matchPattern(pathname, p.pattern)) return p
  }
  return null
}

/**
 * SPARouter — watches for SPA route changes via popstate + pushState/replaceState interception.
 * Calls the provided callback whenever the pathname changes.
 */
export class SPARouter {
  private currentPath: string
  private onChange: (newPath: string) => void
  private origPushState: typeof history.pushState
  private origReplaceState: typeof history.replaceState

  constructor(onChange: (newPath: string) => void) {
    this.currentPath = location.pathname
    this.onChange = onChange

    // Save originals
    this.origPushState = history.pushState.bind(history)
    this.origReplaceState = history.replaceState.bind(history)

    // Intercept pushState
    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      this.origPushState(...args)
      this.check()
    }

    // Intercept replaceState
    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      this.origReplaceState(...args)
      this.check()
    }

    // Listen for popstate (back/forward)
    window.addEventListener('popstate', () => this.check())
  }

  private check(): void {
    const newPath = location.pathname
    if (newPath !== this.currentPath) {
      this.currentPath = newPath
      this.onChange(newPath)
    }
  }

  destroy(): void {
    history.pushState = this.origPushState
    history.replaceState = this.origReplaceState
  }
}
